import { usePostHog } from '@posthog/react'
import { EventName } from 'posthog-js'
import { useRef } from 'react'

import { ObjectType } from '@/models'

import { DebounceType } from './use-debounce'

export const DEFAULT_DEBOUNCED_CAPTURE_DELAY = 1000

export type CaptureEventProps = {
  user_action: {
    screen: string
    name: string
  }
}

export const useCaptureEvent = () => {
  const postHog = usePostHog()
  // NOTE: ref is used here mostly for asynchronous operations like `captureDebouncedEvent`
  // to have correct postHog reference in there
  const postHogRef = useRef<ReturnType<typeof usePostHog>>(postHog)
  postHogRef.current = postHog

  const timers = useRef<Record<string, DebounceType>>({})

  const captureEvent = <E extends EventName | keyof CaptureEventProps>(
    eventName: E,
    properties?: E extends keyof CaptureEventProps ? CaptureEventProps[E] & ObjectType : ObjectType,
  ) => {
    postHogRef.current?.capture(eventName, properties)
  }

  const captureDebouncedEvent = <E extends EventName | keyof CaptureEventProps>(
    eventName: E,
    properties?: E extends keyof CaptureEventProps ? CaptureEventProps[E] & ObjectType : ObjectType,
    delay: number = DEFAULT_DEBOUNCED_CAPTURE_DELAY,
  ) => {
    const name = properties?.name ?? ''
    const key = `${eventName}-${name}`

    if (timers.current[key]) clearTimeout(timers.current[key])

    timers.current[key] = setTimeout(() => {
      captureEvent(eventName, properties)
      delete timers.current[key]
    }, delay)
  }

  return { captureDebouncedEvent, captureEvent }
}
